import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { NotificationMessageIcons } from '../../../components'
import { envConfig } from '../../../config/envConfig'
import useRetries from '../../../hooks/useRetries'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import {
  addInvoiceCycleProcess,
  removeActiveProcess
} from '../../../redux/actions/processing.actions'
import { selectActiveProcesses } from '../../../redux/selectors/processing.selectors'
import { fetchInvoiceCycleExecution } from '../invoiceCycles.service'

/** Pantallas que muestran su propio faldón/seguimiento de la ejecución (no necesitan el mini-layer) */
const SCREENS_WITH_OWN_TRACKING: string[] = [
  NonAuthRoutes.invoiceCycleProcessing,
  NonAuthRoutes.Settlements,
  NonAuthRoutes.SettlementsInvoices,
  NonAuthRoutes.SettlementsAnnulation
]

type ExecutionStatusNotificationCase = 'processing' | 'finished' | 'paused' | 'closed'

interface UseExecutionStatusNotificationParams {
  executionCycleId: number
  /** Estado actual de la ejecución según la pantalla (null mientras no se ha cargado) */
  status: EnumEstadoCiclo | null
  /** La pantalla actualiza su estado de ejecución cuando el polling detecta un cambio */
  onStatusChange: (status: EnumEstadoCiclo) => void
  /** La pantalla recarga la ejecución y su contenido */
  onRefresh: () => void | Promise<void>
  /** Navega a la pantalla Procesando */
  onViewProgress: () => void
  /**
   * Estado desde el que se lanzó algo (ej. Regenerar) y el back todavía puede no haber pasado a Procesando.
   * Mientras la ejecución siga en ese estado se espera que arranque en lugar de darla por finalizada.
   */
  initialOriginState?: EnumEstadoCiclo
  /** Datos para el mini-layer si el usuario sale de la pantalla mientras la ejecución procesa */
  processInfo?: {
    invoiceCycleId?: number
    cycleName?: string
    billingType?: number
  }
}

/**
 * Hook que gestiona el faldón con el estado de la ejecución en las pantallas de validación
 * (Validar listado / Validar facturas):
 * - Procesando: polling con reintentos limitados. No hay faldón: la pantalla muestra el estado y las
 *   acciones (ver progreso / actualizar) en el bloque En curso de los KPIs.
 * - Cambió de estado tras procesar: faldón con la acción de actualizar (sin navegar solo).
 * - Fin de reintentos: faldón de pausa con la acción de comprobar de nuevo.
 * - Cerrada: faldón informativo, la pantalla pasa a solo lectura.
 *
 * El polling solo corre mientras la ejecución está Procesando (o esperando que arranque).
 *
 * Mini-layer: si el usuario sale de la pantalla (a otra que no sea de seguimiento de la ejecución)
 * mientras procesa, el seguimiento pasa al proceso flotante. Al entrar, se retira el proceso flotante
 * de esa ejecución porque el faldón toma el relevo.
 *
 * @returns
 * - notification: para la prop `notification` de Page (undefined si no hay nada que mostrar)
 * - isProcessingCase, isPausedCase, isFinishedCase, handleViewProgress, handleRefresh, handleResume: para el bloque En
 *   curso mientras procesa o con el polling en pausa (sin faldón)
 * - startWaitingForProcessing: tras lanzar algo que pasará la ejecución por Procesando, indica el
 *   estado de origen para no confundir "todavía no arrancó" con "ya finalizó"
 */
export const useExecutionStatusNotification = ({
  executionCycleId,
  status,
  onStatusChange,
  onRefresh,
  onViewProgress,
  initialOriginState,
  processInfo
}: UseExecutionStatusNotificationParams) => {
  const [t] = useTranslation(['settlementsInvoices'])
  const dispatch = useDispatch()
  const history = useHistory()
  const activeProcesses = useSelector(selectActiveProcesses)
  const [isPaused, setIsPaused] = useState<boolean>(false)
  const [hasPendingChanges, setHasPendingChanges] = useState<boolean>(false)
  const [dismissedCase, setDismissedCase] = useState<ExecutionStatusNotificationCase | null>(null)
  const [awaitingOriginState, setAwaitingOriginState] = useState<EnumEstadoCiclo | null>(
    initialOriginState ?? null
  )

  // Si la ejecución sigue en el estado de origen, se considera Procesando hasta confirmar que arrancó
  const effectiveStatus =
    awaitingOriginState !== null && status === awaitingOriginState
      ? EnumEstadoCiclo.Procesando
      : status
  const isProcessing = effectiveStatus === EnumEstadoCiclo.Procesando

  const [startRetriesTimer, stopRetriesTimer] = useRetries(
    async () => {
      const response = await fetchInvoiceCycleExecution(executionCycleId)
      if (!response.success || !response.data) {
        return
      }

      const newStatus = response.data.status as EnumEstadoCiclo

      if (newStatus === EnumEstadoCiclo.Procesando) {
        // Ya arrancó: se sigue consultando hasta que cambie de estado
        if (status !== EnumEstadoCiclo.Procesando) {
          onStatusChange(newStatus)
        }
        setAwaitingOriginState(null)
        return
      }

      if (awaitingOriginState !== null && newStatus === awaitingOriginState) {
        // Todavía no arrancó
        return
      }

      stopRetriesTimer()
      setAwaitingOriginState(null)
      setHasPendingChanges(true)
      onStatusChange(newStatus)
    },
    envConfig.invoiceCycleConfig.pollingDelay,
    {
      immediate: true,
      retries: envConfig.invoiceCycleConfig.pollingMaxRetries,
      onFinishRetries: () => setIsPaused(true)
    }
  )

  useEffect(() => {
    if (isProcessing && !isPaused) {
      startRetriesTimer()
    } else {
      stopRetriesTimer()
    }
    return () => {
      stopRetriesTimer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing, isPaused])

  // Al entrar, el faldón toma el relevo del mini-layer de esta ejecución (evita dos seguimientos)
  useEffect(() => {
    activeProcesses.forEach(process => {
      if (process.processType === 'invoiceCycle' && process.executionCycleId === executionCycleId) {
        dispatch(removeActiveProcess(process.processId))
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Al salir mientras procesa, el seguimiento pasa al mini-layer. Se lee desde un ref (valores al desmontar)
  const handoffRef = useRef({ isProcessing, awaitingOriginState, processInfo })
  handoffRef.current = { isProcessing, awaitingOriginState, processInfo }

  useEffect(() => {
    return () => {
      const current = handoffRef.current
      if (!current.isProcessing || SCREENS_WITH_OWN_TRACKING.includes(history.location.pathname)) {
        return
      }
      dispatch(
        addInvoiceCycleProcess(
          executionCycleId,
          EnumEstadoCiclo.Procesando,
          {
            invoiceCycleId: current.processInfo?.invoiceCycleId,
            billingType: current.processInfo?.billingType,
            originState: current.awaitingOriginState ?? undefined
          },
          current.processInfo?.cycleName
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startWaitingForProcessing = (originState: EnumEstadoCiclo) => {
    setAwaitingOriginState(originState)
    setIsPaused(false)
    setHasPendingChanges(false)
    setDismissedCase(null)
  }

  const handleRefresh = async () => {
    await onRefresh()
    setHasPendingChanges(false)
    setIsPaused(false)
    setDismissedCase(null)
  }

  const handleResume = async () => {
    if (awaitingOriginState === null) {
      setIsPaused(false)
      return
    }

    // Tras agotar los reintentos esperando el arranque: una lectura inmediata del estado real
    setAwaitingOriginState(null)
    const response = await fetchInvoiceCycleExecution(executionCycleId)
    if (response.success && response.data) {
      const currentStatus = response.data.status as EnumEstadoCiclo
      setHasPendingChanges(currentStatus !== EnumEstadoCiclo.Procesando)
      onStatusChange(currentStatus)
    }
    setIsPaused(false)
  }

  const getCurrentCase = (): ExecutionStatusNotificationCase | null => {
    if (status === EnumEstadoCiclo.Finalizado) return 'closed'
    if (isProcessing) return isPaused ? 'paused' : 'processing'
    if (hasPendingChanges) return 'finished'
    return null
  }

  const currentCase = getCurrentCase()

  const getNotification = (): GlobalNotification | undefined => {
    if (currentCase === null || currentCase === dismissedCase) {
      return undefined
    }

    const onClose = () => setDismissedCase(currentCase)

    switch (currentCase) {
      case 'closed':
        return {
          message: t('settlementsInvoices:globalNotifications.executionStatus.closed.message'),
          icon: NotificationMessageIcons.INFO,
          onClose
        }
      default:
        return undefined
    }
  }

  return {
    notification: getNotification(),
    startWaitingForProcessing,
    /** Procesando y comprobando: el seguimiento se muestra en el bloque 'En curso' de los KPIs, no en el faldón */
    isProcessingCase: currentCase === 'processing' || currentCase === 'paused',
    /** Se agotaron los reintentos del polling: el bloque ofrece comprobar de nuevo */
    isPausedCase: currentCase === 'paused',
    /** Terminó de procesar mientras la pantalla lo seguía: el bloque invita a actualizar la información */
    isFinishedCase: currentCase === 'finished',
    handleResume,
    handleViewProgress: onViewProgress,
    handleRefresh
  }
}
